// Service to handle routing adaptations between legacy and database-first approaches

/**
 * Formats a job ID for use in the URL route to the job monitoring page.
 * 
 * This handles the difference between:
 * - Legacy mode: Uses string-based job IDs (e.g., 'batch-123abc')
 * - Database mode: Uses numeric workflow IDs or module IDs
 * 
 * @param id - The job ID, workflow ID, or module ID
 * @returns A correctly formatted ID string for the route
 */
export function formatJobIdForRoute(id: string | number | undefined): string {
  if (!id) {
    return 'unknown';
  }
  
  // If it's already a string, just return it
  if (typeof id === 'string') {
    return id;
  }
  
  // If it's a number, prefix it to help identify it's a database ID
  return `db-${id}`;
}

/**
 * Parses a job ID from the route parameter.
 * 
 * This handles both legacy string IDs and database numeric IDs.
 * 
 * @param routeId - The ID from the route parameter
 * @returns An object with parsed ID information
 */
export function parseJobIdFromRoute(routeId: string | undefined): {
  isDatabase: boolean;
  numericId: number | null;
  originalId: string;
} {
  if (!routeId) {
    return {
      isDatabase: false,
      numericId: null,
      originalId: 'unknown'
    };
  }
  
  // Check if this is a database-formatted ID (db-123)
  if (routeId.startsWith('db-')) {
    const numericPart = routeId.slice(3); // Remove 'db-' prefix
    const parsedId = parseInt(numericPart, 10);
    
    return {
      isDatabase: true,
      numericId: isNaN(parsedId) ? null : parsedId,
      originalId: routeId
    };
  }
  
  // For legacy IDs, just return as is
  return {
    isDatabase: false,
    numericId: null,
    originalId: routeId
  };
}
